import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import BootstrapTable from 'react-bootstrap-table-next';
import cellEditFactory, { Type } from 'react-bootstrap-table2-editor';
import styles from './StoresTable.module.scss';
import Toolbar from '../../common/Toolbar';
import ConfirmModal from '../../modals/ConfirmDeleteModal';
import { formatMany } from '../../../utils/formatHelper';
import { countries, states } from '../../../constants/country';

class StoresTable extends PureComponent {
  constructor(props) {
    super(props);

    this.non_admin_users = props.users
      .filter(({ administration_permission }) => administration_permission === 'none')
      .map(({ user }) => user.id);

    this.state = {
      addNewStore: false,
      confirm: {
        open: false,
        text: '',
      },
    };
  }

  newStore() {
    return {
      id: 'new',
      name: '',
      address1: '',
      city: '',
      state: 'AL',
      country: 'US',
      zip_code: '',
    };
  }

  addStore = () => {
    this.setState((prevState) => {
      return {
        ...prevState,
        addNewStore: true,
      };
    });
  };

  confirmDeleteStore = (storeId) => {
    const { data } = this.props;
    const store = data.find(({ id }) => id === storeId);

    this.setState({
      confirm: {
        open: true,
        text: `Are you sure you want to delete this store? (${store.name})`,
        storeId: storeId,
      },
    });
  };

  closeDeleteStore = () => {
    this.setState({
      confirm: {
        open: false,
      },
    });
  };

  deleteStore = (storeId) => {
    this.closeDeleteStore();
    this.props.deleteStore(storeId);
  };

  countUsers = (row) => {
    if (row.user_stores) {
      const users = row.user_stores.filter(({ user_id }) => this.non_admin_users.includes(user_id));
      return `${users.length} ${formatMany(users.length, 'user')}`;
    } else {
      return '0 users';
    }
  };

  userFormatter = (id, row) => {
    const { openModal } = this.props;

    if (id === 'new') {
      return <span>-</span>;
    }

    if (!this.non_admin_users.length) {
      return <span title="All Admin users have access to the store">-</span>;
    }

    return (
      <div className={styles.users}>
        <span className={styles.editUser} onClick={() => openModal(row)}>
          {this.countUsers(row)}
        </span>
      </div>
    );
  };

  actionsFormatter = (cell, row, rowIndex, { confirmDeleteStore }) => {
    const storeId = row.id;
    if (storeId === 'new') {
      return <span>-</span>;
    }

    return (
      <div className={styles.actions}>
        <span className="ic-bin" onClick={() => confirmDeleteStore(storeId)} />
      </div>
    );
  };

  validateStore(id, store) {
    const { alertError } = this.props;
    const { address1, city, name, zip_code, country, state } = store;

    if (address1.length * city.length * state.length * name.length * zip_code.length * country.length === 0) {
      if (id !== 'new') {
        const error = new Error('All fields should be mandatory');
        alertError(error);
      }
      return false;
    }

    const currentStates = states[country];
    const validState = currentStates.some((item) => item.value === state);

    if (!validState) {
      const error = new Error('State must be in country');
      alertError(error);
      return false;
    }

    return true;
  }

  requiredValidator = (newValue, row, column) => {
    if (newValue === '') {
      return {
        valid: false,
        message: 'Required',
      };
    }

    return true;
  };

  render() {
    const { data } = this.props;
    const { confirm, addNewStore } = this.state;

    const storesData = addNewStore ? [this.newStore(), ...data] : data;

    const columns = [
      {
        text: 'Store Name',
        dataField: 'name',
        validator: this.requiredValidator,
      },
      {
        text: 'Address',
        dataField: 'address1',
        validator: this.requiredValidator,
      },
      {
        text: 'City',
        dataField: 'city',
        validator: this.requiredValidator,
      },
      {
        text: 'State',
        dataField: 'state',
        formatter: (cell, row) => {
          const state = states[row.country].find(({ value }) => value === cell);
          return <span>{state ? state.label : cell}</span>;
        },
        editor: {
          type: Type.SELECT,
          getOptions: (setOptions, params) => {
            return states[params.row.country];
          },
        },
      },
      {
        text: 'Country',
        dataField: 'country',
        formatter: (cell) => countries.find((country) => country.value === cell).label,
        editor: {
          type: Type.SELECT,
          options: countries,
        },
      },
      {
        text: 'ZIP Code',
        dataField: 'zip_code',
        validator: this.requiredValidator,
      },
      {
        text: 'Users',
        dataField: 'id',
        formatter: this.userFormatter,
      },
      {
        text: 'Action',
        isDummyField: true,
        dataField: 'actions',
        formatter: this.actionsFormatter,
        formatExtraData: {
          confirmDeleteStore: this.confirmDeleteStore,
        },
        editable: false,
      },
    ];

    const cellEdit = cellEditFactory({
      mode: 'click',
      blurToSave: true,
      afterSaveCell: (oldValue, newValue, row, column) => {
        if (oldValue !== newValue) {
          const { id, name, address1, city, state, zip_code, country } = row;

          const payload = {
            name,
            address1,
            city,
            country,
            state,
            zip_code,
          };

          if (this.validateStore(id, payload)) {
            if (id === 'new') {
              this.props.submitNewStore(payload);
              this.setState((prevState) => {
                return {
                  ...prevState,
                  addNewStore: false,
                };
              });
            } else {
              this.props.save(id, payload);
            }
          }
        }
      },
    });

    return (
      <div>
        <Toolbar name="Stores">
          {!addNewStore && (
            <button type="button" className={styles.link} onClick={this.addStore}>
              Add a Store
            </button>
          )}
        </Toolbar>

        <BootstrapTable
          data={storesData}
          columns={columns}
          keyField="id"
          striped
          bordered={false}
          hover
          className={styles.container}
          cellEdit={cellEdit}
        ></BootstrapTable>

        <ConfirmModal
          open={confirm.open}
          text={confirm.text}
          objectId={confirm.storeId}
          onConfirm={this.deleteStore}
          onCancel={this.closeDeleteStore}
        />
      </div>
    );
  }
}

export default StoresTable;

StoresTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  openModal: PropTypes.func.isRequired,
  save: PropTypes.func.isRequired,
  submitNewStore: PropTypes.func.isRequired,
  deleteStore: PropTypes.func.isRequired,
  alertError: PropTypes.func.isRequired,
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
};
